/* eslint-disable better-mutation/no-mutation */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { normalize } from './helpers';
import { type DiscoverState, initialState } from './initial-state';
import type {
    CategoryLinkViewModel,
    ItemCardViewModel,
    SearchPageApiResponse
} from 'tradera/state/services/types/webapi-discover-generated';
import type { QueryParams } from 'tradera/apps/tradera/components/discover/components/filters/types';

const slice = createSlice({
    name: 'discover',
    initialState,
    reducers: {
        initialize: (_state, { payload }: PayloadAction<DiscoverState>) => {
            return { ...payload };
        },
        requestSearchResults: (state) => {
            state.error = null;
        },
        receiveSearchResults: (
            state,
            {
                payload
            }: PayloadAction<{
                result: SearchPageApiResponse & {
                    categoryPath?: CategoryLinkViewModel[];
                };
                endpoint?: string | null;
                queryParams?: QueryParams;
                shippingRegionCountryCodeIso2?: string;
            }>
        ) => {
            const {
                result,
                endpoint = null,
                queryParams = null,
                shippingRegionCountryCodeIso2 = 'se'
            } = payload;

            return {
                ...state,
                endpoint,
                error: null,
                hasResult: true,
                queryParams,
                ...normalize(result, shippingRegionCountryCodeIso2)
            };
        },
        handleSearchResultsError: (
            state,
            { payload }: PayloadAction<string>
        ) => {
            const normalized = normalize({});
            state.error = payload;
            state.hasResult = true;
            Object.assign(state, normalized);
        },
        setSingleFilterValue: (
            state,
            {
                payload
            }: PayloadAction<{
                filter: keyof NonNullable<DiscoverState['filters']>;
                value: string | undefined;
            }>
        ) => {
            const currentFilter = state.filters?.[payload.filter];

            state.filters = {
                ...state.filters,
                [payload.filter]: {
                    ...currentFilter,
                    selectedValue: payload.value,
                    // TS complains about types being either boolean or undefined, so this is to make tsc happy
                    isIndexable: !!currentFilter?.isIndexable,
                    translateOptions: !!currentFilter?.translateOptions
                }
            };
        },
        updateItem: (
            state,
            {
                payload
            }: PayloadAction<{
                itemId: number;
                onDisplay: boolean;
                discountRate: number;
                price: number;
            }>
        ) => {
            const { itemId, ...itemData } = payload;
            return {
                ...state,
                items: state.items.map((x) =>
                    x.itemId === itemId ? { ...x, ...itemData } : x
                ),
                itemsOnDisplay: state.itemsOnDisplay.map((x) =>
                    x.itemId === itemId ? { ...x, ...itemData } : x
                ),
                relatedItems: state.relatedItems.map((x) =>
                    x.itemId === itemId ? { ...x, ...itemData } : x
                )
            };
        },
        addItemOnDisplay: (
            state,
            {
                payload
            }: PayloadAction<{
                item: ItemCardViewModel;
            }>
        ) => {
            return state.itemsOnDisplay.some(
                (x) => x.itemId === payload.item.itemId
            )
                ? state
                : {
                      ...state,
                      itemsOnDisplay: [...state.itemsOnDisplay, payload.item]
                  };
        },
        removeItemOnDisplay: (
            state,
            {
                payload
            }: PayloadAction<{
                item: ItemCardViewModel;
            }>
        ) => {
            return state.itemsOnDisplay.some(
                (x) => x.itemId === payload.item.itemId
            )
                ? {
                      ...state,
                      itemsOnDisplay: state.itemsOnDisplay.filter(
                          (x) => x.itemId !== payload.item.itemId
                      )
                  }
                : state;
        },
        setCategoryPath: (
            state,
            { payload }: PayloadAction<CategoryLinkViewModel[]>
        ) => {
            state.categoryPath = payload;
            state.currentCategoryId = (payload && payload[0].id) || 0;
        },
        setCurrentCategoryId: (state, { payload }: PayloadAction<number>) => {
            state.currentCategoryId = payload;
        },
        setIsFilterOpen: (state, { payload }: PayloadAction<boolean>) => {
            state.isFilterOpen = payload;
        },
        reset: (
            state,
            { payload }: PayloadAction<{ initialState: DiscoverState }>
        ) => {
            Object.assign(state, payload.initialState);
        },
        setWardrobeProfileEnabled: (
            state,
            { payload }: PayloadAction<boolean>
        ) => {
            if (state?.wardrobeProfile) {
                state.wardrobeProfile.isEnabled = payload;
            }
        }
    }
});

export const {
    initialize,
    requestSearchResults,
    receiveSearchResults,
    handleSearchResultsError,
    setSingleFilterValue,
    updateItem,
    addItemOnDisplay,
    removeItemOnDisplay,
    setCategoryPath,
    setIsFilterOpen,
    setCurrentCategoryId,
    reset,
    setWardrobeProfileEnabled
} = slice.actions;
export const reducer = slice.reducer;
